import { create } from 'zustand';
import { ListNews, CreateNews, GetNews, UpdateNews, DeleteNews } from '../api/api';
import toast from 'react-hot-toast';

export interface News {
  _id: string;
  title: string;
  description: string;
  fullContent: string;
  image?: string;
  date: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateNewsI {
  title: string;
  description: string;
  fullContent: string;
  image?: string;
  date: string;
}

interface UpdateNewsI {
  title?: string;
  description?: string;
  fullContent?: string;
  image?: string;
  date?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetNewsResponse {
  data: {
    news: News[];
    pagination?: PaginationInfo;
  };
}

interface NewsResponse {
  data: {
    news: News;
  };
  status: boolean;
  message: string;
}

interface DeleteNewsResponse {
  status: boolean;
  message: string;
}

interface NewsStore {
  news: News[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getNews: (isPublic?: boolean, search?: string, page?: number, limit?: number) => Promise<void>;
  createNews: (news: CreateNewsI) => Promise<void>;
  getNewsById: (id: string) => Promise<News | null>;
  updateNews: (id: string, news: UpdateNewsI) => Promise<void>;
  deleteNews: (id: string) => Promise<void>;
}

export const useNewsStore = create<NewsStore>((set, get) => ({
  news: [] as News[],
  loading: false,
  error: null,
  pagination: null,

  getNews: async (isPublic?: boolean, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListNews<GetNewsResponse>(isPublic, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      news: data.data.news || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createNews: async (news: CreateNewsI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateNews<NewsResponse>(news);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create news' });
      return;
    }

    const newsList = get().news;
    set({ news: [data.data.news, ...newsList], loading: false });
    toast.success('News created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getNews(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  getNewsById: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetNews<NewsResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get news' });
      return null;
    }

    set({ loading: false });
    return data.data.news;
  },

  updateNews: async (id: string, news: UpdateNewsI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateNews<NewsResponse>(id, news);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update news' });
      return;
    }

    const newsList = get().news;
    const updatedNews = newsList.map((n) => (n._id === id ? { ...n, ...data.data.news } : n));
    set({ news: updatedNews, loading: false });
    toast.success('News updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getNews(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  deleteNews: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteNews<DeleteNewsResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete news' });
      return;
    }

    const newsList = get().news.filter((newsItem) => newsItem._id !== id);
    set({ news: newsList, loading: false });
    toast.success('News deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getNews(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { CreateNewsI, UpdateNewsI };
